import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLab, nameA, nameB, isSolo } from "@/context/LabContext";
import { PositionsVsNeedsDialog } from "@/components/PositionsVsNeedsDialog";
import MicButton from "@/components/MicButton";

export default function NeedsStep() {
  const { map, update, setStep } = useLab();
  const [loading, setLoading] = useState(false);
  const [solving, setSolving] = useState(false);
  const solo = isSolo(map);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/needs", {
        issue: map.issue,
        position_a: map.position_a,
        position_b: map.position_b,
        concerns_a: map.concerns_a,
        concerns_b: map.concerns_b,
        name_a: nameA(map),
        name_b: nameB(map),
      });
      update({ needs_a: data.needs_a || [], needs_b: data.needs_b || [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => {
    if ((map.needs_a?.length || 0) === 0 && (map.needs_b?.length || 0) === 0) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goSolutions = async () => {
    if (!map.needs_a.length || !map.needs_b.length) {
      toast.error("Add at least one need for each partner first.");
      return;
    }
    if (map.solutions?.length) {
      setStep(3);
      return;
    }
    setSolving(true);
    try {
      const { data } = await api.post("/ai/solutions", {
        issue: map.issue,
        position_a: map.position_a,
        position_b: map.position_b,
        needs_a: map.needs_a,
        needs_b: map.needs_b,
        name_a: nameA(map),
        name_b: nameB(map),
      });
      update({ solutions: data.solutions || [] });
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSolving(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2D2A26]">What you each need</h2>
        <div className="flex items-center gap-2 shrink-0">
          <PositionsVsNeedsDialog />
        </div>
      </div>
      <p className="text-[#6E6860] mb-4">
        Beneath every position are real, legitimate needs — the things each of you cares about deep down.
        {" "}{nameA(map)}'s needs are on the left, {nameB(map)}'s on the right.
        {solo
          ? ` These are a best reading of each side — including your guess at ${nameB(map)}'s. Edit, add, or remove any so they feel as true as you can make them.`
          : " Read them together, and each of you edit, add, or remove any so they feel true to you."}
      </p>
      <button
        onClick={generate}
        disabled={loading}
        data-testid="ai-suggest-needs-button"
        className="mb-8 inline-flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#3B4A3E] underline underline-offset-4 disabled:opacity-60"
      >
        <Sparkles className={`h-4 w-4 ${loading ? "animate-pulse" : ""}`} /> Ask AI to suggest needs again
      </button>

      {loading ? (
        <Loading />
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          <NeedsColumn
            needsTitle={`${nameA(map)}'s needs`}
            accent="#4A5D4E"
            position={map.position_a}
            needs={map.needs_a}
            side="a"
            onChange={(arr) => update({ needs_a: arr })}
          />
          <NeedsColumn
            needsTitle={`${nameB(map)}'s needs`}
            accent="#C4A265"
            position={map.position_b}
            needs={map.needs_b}
            side="b"
            onChange={(arr) => update({ needs_b: arr })}
          />
        </div>
      )}

      <div className="mt-10 flex justify-between">
        <button onClick={() => setStep(1)} data-testid="needs-back-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[#6E6860] hover:bg-[#F5F2EA] transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          onClick={goSolutions}
          disabled={solving || loading}
          data-testid="generate-solutions-button"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 disabled:opacity-60"
        >
          {solving ? "Finding ways forward…" : map.solutions?.length ? "Continue to ways forward" : "Find ways forward"}
          {!solving && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
      {solving ? (
        <p className="mt-4 text-center text-sm text-[#6E6860]" data-testid="solutions-wait-hint">
          Thinking this through carefully — this may take a minute or two. Thanks for your patience.
        </p>
      ) : (
        <p className="mt-4 text-center text-sm text-[#A39E94]" data-testid="solutions-wait-note">
          Heads up: finding thoughtful ways forward can take a minute or two.
        </p>
      )}
    </motion.div>
  );
}

function NeedsColumn({ needsTitle, accent, position, needs, side, onChange }) {
  const [draft, setDraft] = useState("");
  const remove = (i) => onChange(needs.filter((_, idx) => idx !== i));
  const edit = (i, val) => onChange(needs.map((n, idx) => (idx === i ? val : n)));
  const add = () => {
    if (!draft.trim()) return;
    onChange([...needs, draft.trim()]);
    setDraft("");
  };

  return (
    <div className="rounded-2xl bg-white border border-[#E8E3D9] p-6">
      <p className="text-sm uppercase tracking-[0.2em] mb-1" style={{ color: accent }}>{needsTitle}</p>
      <p className="text-sm text-[#6E6860] mb-4 line-clamp-2">{position}</p>
      <div className="space-y-2.5 pt-4 border-t border-[#E8E3D9]">
        {needs.map((n, i) => (
          <NeedItem
            key={i}
            value={n}
            onChange={(val) => edit(i, val)}
            onRemove={() => remove(i)}
            testId={`need-chip-${side}-${i}`}
            removeTestId={`need-remove-${side}-${i}`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a need…"
          data-testid={`need-add-input-${side}`}
          className="flex-1 rounded-full border border-[#E8E3D9] bg-white px-4 py-1.5 text-sm text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition"
        />
        <MicButton value={draft} onChange={setDraft} testId={`need-add-mic-${side}`} />
        <button onClick={add} data-testid={`need-add-button-${side}`} className="p-2 rounded-full bg-[#4A5D4E] text-[#FDFBF7] hover:bg-[#3B4A3E] transition-colors">
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function NeedItem({ value, onChange, onRemove, testId, removeTestId }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  }, [value]);
  return (
    <div className="group flex items-start gap-2 rounded-2xl bg-[#F5F2EA] border border-[#E8E3D9] pl-4 pr-2 py-2" data-testid={testId}>
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent text-sm text-[#2D2A26] leading-relaxed focus:outline-none resize-none overflow-hidden"
      />
      <button
        onClick={onRemove}
        data-testid={removeTestId}
        className="mt-0.5 p-1 rounded-full text-[#A39E94] hover:text-[#B27A70] hover:bg-white transition-colors shrink-0"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Loading() {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {[0, 1].map((c) => (
        <div key={c} className="rounded-2xl bg-white border border-[#E8E3D9] p-6 space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-8 rounded-full bg-[#F5F2EA] animate-breathe" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      ))}
    </div>
  );
}
